<!--
Copyright 2017 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <tr :class="htmlClass">
    <td class="form-attachment-list-type">{{ type }}</td>
    <td :title="attachment.name" class="form-attachment-list-name">
      <a v-if="attachment.exists" :href="href" target="_blank">
        {{ attachment.name }}
      </a>
      <template v-else>
        {{ attachment.name }}
      </template>
    </td>
    <td class="form-attachment-list-uploaded">
      <template v-if="attachment.exists">
        <date-time :iso="attachment.updatedAt"/>
        <span v-show="targeted" class="label label-primary">
          {{ $t('replace') }}
        </span>
      </template>
      <template v-else>
        <span class="icon-exclamation-triangle"></span>
        <span :title="$t('notUploaded.title')">
          {{ $t('notUploaded.text') }}
        </span>
      </template>
    </td>
  </tr>
</template>

<script>
import DateTime from '../date-time.vue';
import { apiPaths } from '../../util/request';
import { requestData } from '../../store/modules/request';

export default {
  name: 'FormAttachmentRow',
  components: { DateTime },
  props: {
    attachment: {
      type: Object,
      required: true
    },
    fileIsOverDropZone: {
      type: Boolean,
      default: false
    },
    dragoverAttachment: Object, // eslint-disable-line vue/require-default-prop
    plannedUploads: {
      type: Array,
      required: true
    },
    updatedAttachments: {
      type: Array,
      required: true
    }
  },
  computed: {
    // The component assumes that this data will exist when the component is
    // created.
    ...requestData(['form']),
    targeted() {
      const targetedByDragover = this.dragoverAttachment != null &&
        this.attachment.name === this.dragoverAttachment.name;
      const targetedByDrop = this.plannedUploads
        .some(({ attachment }) => attachment.name === this.attachment.name);
      return targetedByDragover || targetedByDrop;
    },
    htmlClass() {
      const dragoverTargetsADifferentRow = this.dragoverAttachment != null &&
        this.dragoverAttachment.name !== this.attachment.name;
      const highlightedAsInfo = this.targeted ||
        (this.fileIsOverDropZone && !dragoverTargetsADifferentRow);
      const highlightedAsSuccess = this.updatedAttachments
        .some(attachment => attachment.name === this.attachment.name);
      return {
        'form-attachment-row': true,
        info: highlightedAsInfo,
        'form-attachment-row-targeted': this.targeted,
        success: highlightedAsSuccess
      };
    },
    type() {
      const { type } = this.attachment;
      if (!/^\w+$/.test(type)) return type;
      const path = `type.${type}`;
      return this.$te(path, this.$i18n.fallbackLocale) ? this.$t(path) : type;
    },
    href() {
      return apiPaths.formDraftAttachment(
        this.form.projectId,
        this.form.xmlFormId,
        this.attachment.name
      );
    }
  }
};
</script>

<style lang="scss">
@import '../../assets/scss/variables';

#form-attachment-list-table {
  > tbody > tr {
    &.form-attachment-row-targeted {
      > td {
        box-shadow: inset 0 1px $color-info, inset 0 -1px $color-info;

        &:first-child {
          box-shadow: inset 1px 1px $color-info, inset 0 -1px $color-info;
        }

        &:last-child {
          box-shadow: inset 0 1px $color-info, inset -1px -1px $color-info;
        }
      }
    }

    .label {
      margin-left: 5px;
    }

    .icon-exclamation-triangle {
      color: #e1bf50;
      margin-right: 2px;

      + span {
        cursor: help;
      }
    }
  }
}
</style>

<i18n lang="json5">
{
  "en": {
    // This is a type of Media File.
    "type": {
      "image": "Image",
      "audio": "Audio",
      "video": "Video",
      "file": "Data File"
    },
    // This is a label that is shown next to a Media File that would be replaced
    // if the selected files were uploaded.
    "replace": "Replace",
    "notUploaded": {
      // This is shown for a Media File that has not been uploaded.
      "text": "Not yet uploaded",
      "title": "To upload files, drag and drop one or more files onto this page"
    }
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "type": {
      "image": "Obrázek",
      "audio": "Zvuk",
      "video": "Video",
      "file": "Datový soubor"
    },
    "replace": "Nahradit",
    "notUploaded": {
      "text": "Nebyl nahrán",
      "title": "Chcete-li nahrát soubory, přetáhněte jeden nebo více souborů na tuto stránku"
    }
  },
  "de": {
    "type": {
      "image": "Bild",
      "audio": "Audio",
      "video": "Video",
      "file": "Daten-Datei"
    },
    "replace": "Ersetzen",
    "notUploaded": {
      "text": "Noch nicht hochgeladen",
      "title": "Um eine oder mehrere Dateien hochzuladen, verwenden Sie Drag-and-Drop auf diese Seite"
    }
  },
  "es": {
    "type": {
      "image": "Imagen",
      "audio": "Audio",
      "video": "Video",
      "file": "Archivo de datos"
    },
    "replace": "Reemplazar",
    "notUploaded": {
      "text": "Aún no cargado",
      "title": "Para cargar archivos, arrastre y suelte uno o más archivos en esta página."
    }
  },
  "fr": {
    "type": {
      "image": "Image",
      "audio": "Audio",
      "video": "Vidéo",
      "file": "Fichier de données"
    },
    "replace": "Remplacer",
    "notUploaded": {
      "text": "Non encore téléversé",
      "title": "Pour téléverser des fichiers, glissez/déposer un ou plusieurs fichiers sur le tableau de cette page."
    }
  }
}
</i18n>
